import * as usersAPI from "./users-api.js"
import { debug } from "./debug.js"
import { logger } from "./logger"
import { clearSession } from "./session-utils.js"
import { eventBus } from "./event-bus"
import { clearStoredToken, getStoredToken, setStoredToken } from "./token-storage.js"
import { parseJwtPayload } from "./encoding-utils"

/**
 * Signs up a new user and stores their authentication token.
 * Handles Safari private browsing mode where localStorage may not be available.
 *
 * @async
 * @param {Object} userData - User registration data
 * @param {string} userData.name - User's full name
 * @param {string} userData.email - User's email address
 * @param {string} userData.password - User's password
 * @returns {Promise<Object|null>} User object if signup successful, null otherwise
 */
export async function signUp(userData) {
    const token = await usersAPI.signUp(userData);
    try {
        setStoredToken(token);
        
        // Update EventBus with auth token to enable WebSocket connection
        eventBus.setAuthToken(token).catch(err => {
            logger.warn('Failed to update EventBus auth token after signup', { error: err.message });
        });
    } catch (error) {
        logger.warn('Failed to store token in localStorage', { error: error.message });
    }
    return getUser();
}

/**
 * Retrieves the stored authentication token if it exists and is not expired.
 * Handles Safari private browsing mode where localStorage may not be available.
 *
 * @returns {string|null} JWT token string or null if no valid token
 */
export function getToken() {
    try {
        const token = getStoredToken();
        if (!token) return null;
        
        // Validate token format (should be 3 parts separated by dots)
        const parts = token.split('.');
        if (parts.length !== 3) {
            logger.warn('Invalid token format - removing corrupted token', { tokenParts: parts.length });
            clearStoredToken();
            return null;
        }
        
        // Try to decode the payload
        try {
            const payload = parseJwtPayload(parts[1]);
            if (payload.exp < Date.now() / 1000) {
                clearStoredToken();
                return null;
            }
            return token;
        } catch (decodeError) {
            // Token is corrupted or invalid
            logger.warn('Failed to decode token - removing corrupted token', { error: decodeError.message });
            clearStoredToken();
            return null;
        }
    } catch (error) {
        // Handle Safari private browsing mode or other localStorage issues
        logger.warn('localStorage access failed', { error: error.message });
        return null;
    }
}

/**
 * Retrieves the current user from the stored JWT token.
 *
 * @returns {Object|null} User object with id, name, email, or null if not authenticated
 */
export function getUser() {
    const token = getToken();
    debug.log('getUser called, token exists:', !!token);
    let user;
    if (token) {
        user = parseJwtPayload(token.split('.')[1]).user;
        user.experiences = null;
        debug.log('getUser decoded user:', { email: user.email, _id: user._id });
    } else {
        debug.log('getUser: no token found');
    }
    return token ? user : null;
}

/**
 * Logs out the current user by removing their authentication token and clearing session.
 * Handles Safari private browsing mode where localStorage may not be available.
 */
export async function logout() {
    try {
        // Clear session on backend if user is authenticated
        const token = getToken();
        if (token) {
            try {
                await fetch('/api/auth/logout', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
            } catch (error) {
                logger.warn('Failed to clear session on backend', { error: error.message });
            }
        }
        
        // Clear frontend session storage
        clearSession();
        
        // Clear authentication token
        clearStoredToken();
    } catch (error) {
        logger.warn('Logout error', { error: error.message });
        // Still try to clear token even if other operations fail
        try {
            clearStoredToken();
        } catch (storageError) {
            logger.warn('Failed to remove token from localStorage', { error: storageError.message });
        }
    }
}

export async function login(credentials) {
    debug.log('users-service login called with credentials:', { email: credentials.email });
    const token = await usersAPI.login(credentials);
    debug.log('users-service login got token:', token ? 'token received' : 'no token');
    try {
        setStoredToken(token);
        debug.log('Token stored in localStorage');
        
        // Update EventBus with auth token to enable WebSocket connection
        // This is necessary because EventBus initializes before user logs in
        eventBus.setAuthToken(token).catch(err => {
            logger.warn('Failed to update EventBus auth token', { error: err.message });
        });
    } catch (error) {
        logger.warn('Failed to store token in localStorage', { error: error.message });
    }
    const user = getUser();
    debug.log('users-service login returning user:', user ? { email: user.email, _id: user._id } : null);
    return user;
}

export async function checkToken() {
    return usersAPI.checkToken().then(dateStr => new Date(dateStr))
}

/**
 * Updates the stored authentication token with new user data.
 * Used when user profile is updated to keep localStorage in sync.
 *
 * @param {string} token - New JWT token to store
 */
export function updateToken(token) {
    try {
        setStoredToken(token);
    } catch (error) {
        logger.warn('Failed to update token in localStorage', { error: error.message });
    }
}